import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { losApi } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { fmt$, ModuleHeader, Fld, SelectFld } from '../shared/formFields';
import { OTHER_INCOME_TYPES, otherIncomeLabel } from '../shared/otherIncome';
import { AriveLockNote, isAriveManaged, toastAriveConflict } from '../../ariveManaged';

// Loan programs the Income Calculator has agency rules for.
const CALC_PROGRAMS = ['conventional', 'fha', 'va', 'usda'];
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

// 'money_market' -> 'Money market'
const label = (v, empty = 'Account') => (v ? String(v).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : empty);
const monthly = (rows) => rows.reduce((s, r) => s + (Number(r.monthly_gross) || 0), 0);

export function FinancialInfoModule({ loan, onChange }) {
  // URLA1003 stores employment at urla.current_employment[].monthly_gross and
  // everything that is not a paycheck at urla.other_income[] (type + monthly_gross)
  const employment = loan.urla?.current_employment || [];
  const otherIncome = loan.urla?.other_income || [];
  const navigate = useNavigate();
  const locked = isAriveManaged(loan); // the 1003 sections here are Arive's (ticket 192, D25)
  const analysis = loan.income_analysis;
  const [adding, setAdding] = useState(null); // { other_income_type, monthly_gross } while the add form is open
  const [busy, setBusy] = useState(false);
  const assets = loan.urla?.assets || [];
  const liabilities = loan.urla?.liabilities || [];
  const reo = loan.urla?.reo_properties || [];
  const totalIncome = monthly(employment) + monthly(otherIncome);
  const totalAssets = assets.reduce((s, a) => s + (Number(a.balance) || 0), 0);
  const monthlyLiab = liabilities.reduce((s, l) => s + (Number(l.monthly_payment) || 0), 0);
  const monthlyPaidOff = liabilities.reduce((s, l) => s + (l.to_be_paid_off ? Number(l.monthly_payment) || 0 : 0), 0);

  // Opens the Income Calculator (ticket 139) with this loan's borrower preselected.
  const analyzeIncome = () => {
    const b = loan.urla?.borrower || {};
    const q = new URLSearchParams({ loan: loan.loan_file_id });
    const name = [b.first_name, b.last_name].filter(Boolean).join(' ');
    if (name) q.set('borrower', name);
    if (CALC_PROGRAMS.includes(loan.loan_program)) q.set('loan_type', loan.loan_program);
    navigate(`/portal/income?${q.toString()}`);
  };

  const saveOtherIncome = async (rows, done) => {
    setBusy(true);
    try {
      await losApi.updateUrla(loan.loan_file_id, { ...loan.urla, other_income: rows });
      toast({ title: 'Income saved' });
      done?.();
      onChange?.();
    } catch (e) { if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e) }); }
    finally { setBusy(false); }
  };
  const addOtherIncome = () => {
    const amount = Number(adding?.monthly_gross) || 0;
    const description = adding?.other_income_description?.trim() || '';
    if (!adding?.other_income_type || (adding.other_income_type === 'other' && !description) || amount <= 0) {
      toast({ title: 'Pick a source and enter a monthly amount' });
      return;
    }
    const row = { other_income_type: adding.other_income_type, monthly_gross: amount, is_current: true };
    if (adding.other_income_type === 'other') row.other_income_description = description;
    saveOtherIncome([...otherIncome, row], () => setAdding(null));
  };
  const removeOtherIncome = (i) => saveOtherIncome(otherIncome.filter((_, j) => j !== i));

  // Per-liability "paid off at closing" flag, saved through the 1003.
  const togglePaidOff = async (i) => {
    setBusy(true);
    try {
      const next = liabilities.map((l, j) => (j === i ? { ...l, to_be_paid_off: !l.to_be_paid_off } : l));
      await losApi.updateUrla(loan.loan_file_id, { ...loan.urla, liabilities: next });
      onChange?.();
    } catch (e) { if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e) }); }
    finally { setBusy(false); }
  };

  const skipped = !!loan.income_check_skipped_at;
  const setSkip = async (value) => {
    setBusy(true);
    try {
      await losApi.setIncomeCheckSkip(loan.loan_file_id, value);
      onChange?.();
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e) }); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <ModuleHeader title="Financial Info" extraButtons={
        <>
          <button className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50" data-testid="fin-order-credit">Order Credit</button>
          <button className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: '#070B5F' }} data-testid="fin-analyze" onClick={analyzeIncome}>
            Analyze Income
          </button>
        </>
      } />
      <AriveLockNote loan={loan} testId="fin-arive-note">Income, assets and liabilities on the 1003 are edited in Arive. Income analysis stays here.</AriveLockNote>

      {/* Income summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Monthly Income</div>
            <div className="text-2xl font-bold" style={{ color: '#6BA82E' }}>{fmt$(totalIncome)}</div>
          </div>
          <div className="flex items-center gap-2">
            <button className="text-[12px] font-semibold hover:underline" style={{ color: '#070B5F' }} onClick={analyzeIncome} data-testid="fin-analyze-link">Analyze</button>
            <span className="text-slate-300">·</span>
            <label className="flex items-center gap-1.5 text-[12px] text-slate-500">
              <input type="checkbox" checked={skipped} disabled={busy} onChange={(e) => setSkip(e.target.checked)} data-testid="fin-skip-income-check" /> Skip Income Check
            </label>
          </div>
        </div>
        {skipped && <div className="mb-3 text-[11px] text-amber-700" data-testid="fin-skip-note">Income check skipped on {fmtDate(loan.income_check_skipped_at)}</div>}
        {analysis && (
          <div className="mb-3 rounded-lg bg-[#F3FAE8] px-3 py-2 text-[12px] text-slate-600" data-testid="fin-analysis">
            <b style={{ color: "#030524" }}>Analyzed: {fmt$(analysis.monthly_income)}/mo</b>
            {' '}· Income Calculator, {fmtDate(analysis.analyzed_at)}
            {analysis.method ? <span className="block text-[11px] text-slate-500">{analysis.method}</span> : null}
            {isAriveManaged(loan) && <span className="block text-[11px] text-slate-500" data-testid="fin-analysis-not-in-arive">Not in Arive</span>}
          </div>
        )}
        <div className="space-y-1.5">
          {employment.length === 0 && otherIncome.length === 0 && <div className="text-[12px] text-slate-400 italic">No income entries yet. Add employment in the URLA (Full 1003) module, or other income below.</div>}
          {employment.map((e, i) => (
            <div key={i} className="flex justify-between items-center py-2 border-t border-slate-100 text-[13px]">
              <div>
                <div className="font-semibold" style={{ color: '#030524' }}>{e.employer_name || 'Employment'}</div>
                <div className="text-[11px] text-slate-500">{e.position || ''} {e.is_current ? '· Current' : ''}</div>
              </div>
              <div className="font-semibold" style={{ color: '#030524' }}>{fmt$(e.monthly_gross)}</div>
            </div>
          ))}
          {otherIncome.map((o, i) => (
            <div key={`other-${i}`} className="flex justify-between items-center py-2 border-t border-slate-100 text-[13px]" data-testid="fin-other-income-row">
              <div>
                <div className="font-semibold" style={{ color: '#030524' }}>{otherIncomeLabel(o)}</div>
                <div className="text-[11px] text-slate-500">Other income</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="font-semibold" style={{ color: '#030524' }}>{fmt$(o.monthly_gross)}</div>
                <button className="text-[11px] text-slate-400 hover:text-red-600" disabled={busy || locked} onClick={() => removeOtherIncome(i)} data-testid="fin-other-income-remove">Remove</button>
              </div>
            </div>
          ))}
        </div>
        {adding ? (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="fin-other-income-form">
            <SelectFld label="Source" value={adding.other_income_type} onChange={(v) => setAdding(a => ({ ...a, other_income_type: v }))} options={OTHER_INCOME_TYPES} testId="fin-other-income-type" />
            {adding.other_income_type === 'other' && (
              <Fld label="Describe" value={adding.other_income_description} onChange={(v) => setAdding(a => ({ ...a, other_income_description: v }))} testId="fin-other-income-description" />
            )}
            <Fld label="Monthly amount" kind="money" value={adding.monthly_gross} onChange={(v) => setAdding(a => ({ ...a, monthly_gross: v }))} testId="fin-other-income-amount" />
            <div className="flex gap-2">
              <button className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: '#070B5F' }} disabled={busy} onClick={addOtherIncome} data-testid="fin-other-income-save">Add</button>
              <button className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200" onClick={() => setAdding(null)}>Cancel</button>
            </div>
          </div>
        ) : !locked && (
          <button className="mt-3 text-[12px] font-semibold hover:underline" style={{ color: '#070B5F' }} onClick={() => setAdding({ other_income_type: '', other_income_description: '', monthly_gross: '' })} data-testid="fin-other-income-add">+ Add other income</button>
        )}
      </div>

      {/* Assets */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total Assets</div>
            <div className="text-2xl font-bold" style={{ color: '#030524' }}>{fmt$(totalAssets)}</div>
          </div>
        </div>
        <div className="space-y-1.5">
          {assets.length === 0 && <div className="text-[12px] text-slate-400 italic">No assets recorded.</div>}
          {assets.map((a, i) => (
            <div key={i} className="flex justify-between items-center py-2 border-t border-slate-100 text-[13px]">
              <div>
                <div className="font-semibold" style={{ color: '#030524' }}>{a.institution || label(a.account_type)}</div>
                <div className="text-[11px] text-slate-500">{a.account_number_last4 ? `••••${a.account_number_last4} · ` : ''}{label(a.account_type)}</div>
              </div>
              <div className="font-semibold" style={{ color: '#030524' }}>{fmt$(a.balance)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Liabilities */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Monthly Liability</div>
            <div className="text-2xl font-bold" style={{ color: '#030524' }}>{fmt$(monthlyLiab)}</div>
            {monthlyPaidOff > 0 && <div className="text-[11px] text-slate-500" data-testid="fin-liab-paid-off-note">{fmt$(monthlyPaidOff)} of this is paid off at closing</div>}
          </div>
        </div>
        <div className="space-y-1.5">
          {liabilities.length === 0 && <div className="text-[12px] text-slate-400 italic">No liabilities imported.</div>}
          {liabilities.map((l, i) => (
            <div key={i} className="flex justify-between items-center py-2 border-t border-slate-100 text-[13px]">
              <div>
                <div className="font-semibold flex items-center gap-2" style={{ color: '#030524' }}>
                  {l.creditor}
                  {l.to_be_paid_off && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">PAID OFF AT CLOSING</span>}
                </div>
                <div className="text-[11px] text-slate-500">{label(l.liability_type)} · Bal {fmt$(l.unpaid_balance)}</div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <input type="checkbox" checked={!!l.to_be_paid_off} disabled={busy || locked} onChange={() => togglePaidOff(i)} data-testid="fin-liab-paid-off" /> Paid off
                </label>
                <div className="font-semibold" style={{ color: '#030524' }}>{fmt$(l.monthly_payment)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* REO */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Real Estate Owned</div>
        {reo.length === 0
          ? <div className="text-[12px] text-slate-400 italic">No REO recorded.</div>
          : reo.map((r, i) => (
            <div key={i} className="flex justify-between items-center py-2 border-t border-slate-100 text-[13px]">
              <div className="truncate">
                <div className="font-semibold" style={{ color: '#030524' }}>{r.address?.street || 'Property'}</div>
                <div className="text-[11px] text-slate-500">{label(r.intended_status, 'Status not set')}</div>
              </div>
              <div className="font-semibold" style={{ color: '#030524' }}>{fmt$(r.property_value)}</div>
            </div>
          ))}
      </div>
    </div>
  );
}
